import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Upload, FolderOpen, Zap, Database, Target, Sparkles, TrendingUp, CheckCircle, LogOut, Home } from 'lucide-react';
import { ChatAssistant } from '@/components/ChatAssistant';
import { OnboardingTour } from '@/components/OnboardingTour';
import { HelpCenter } from '@/components/HelpCenter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [healthcareGuideOpen, setHealthcareGuideOpen] = useState(false);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [featureGuideOpen, setFeatureGuideOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('jta_admin_authenticated');
    toast.success('Logged out successfully');
    navigate('/');
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const createProject = async () => {
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/projects?name=${encodeURIComponent(newProject.name)}&description=${encodeURIComponent(newProject.description || '')}`);
      toast.success('Project created successfully');
      setIsCreateOpen(false);
      setNewProject({ name: '', description: '' });
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const healthcareUseCases = {
    clinicalNotes: {
      title: 'Clinical Notes Analysis',
      icon: 'document',
      color: 'blue',
      description: 'Extract medical entities (diagnoses, medications, procedures) from unstructured clinical notes',
      steps: [
        {
          step: 1,
          title: 'Create a Project',
          detail: 'Click "New Project" and name it (e.g., "Clinical Notes Extraction")'
        },
        {
          step: 2,
          title: 'Upload Your Data',
          detail: 'Upload a TXT or CSV file containing clinical notes. Each line should be one note.'
        },
        {
          step: 3,
          title: 'Select Labeling Mode',
          detail: 'Choose "Named Entity Recognition (NER)" from the dropdown'
        },
        {
          step: 4,
          title: 'Add Custom Instructions',
          detail: 'Type: "Extract medical entities including diagnoses, medications, procedures, symptoms, and test results. Format as EntityType:EntityValue"'
        },
        {
          step: 5,
          title: 'Run AI Labeling',
          detail: 'Click "Label All" and GPT-5.1 will extract all medical entities'
        }
      ],
      exampleData: 'Patient presents with acute bronchitis. Prescribed azithromycin 500mg. Chest X-ray ordered.',
      exampleOutput: 'Diagnosis:Acute Bronchitis, Medication:Azithromycin 500mg, Procedure:Chest X-ray',
      labelingMode: 'ner',
      customInstructions: 'Extract medical entities including diagnoses, medications, procedures, symptoms, and test results. Format as EntityType:EntityValue'
    },
    piiDetection: {
      title: 'PII & PHI Detection',
      icon: 'lock',
      color: 'red',
      description: 'Automatically detect Protected Health Information (PHI) and Personally Identifiable Information',
      steps: [
        {
          step: 1,
          title: 'Create a Project',
          detail: 'Click "New Project" and name it (e.g., "PHI Detection")'
        },
        {
          step: 2,
          title: 'Upload Your Data',
          detail: 'Upload medical records or documents containing patient information'
        },
        {
          step: 3,
          title: 'Select Labeling Mode',
          detail: 'Choose "PII Detection" from the dropdown'
        },
        {
          step: 4,
          title: 'Run AI Detection',
          detail: 'Click "Label All" to detect all PHI/PII elements'
        },
        {
          step: 5,
          title: 'Review & Export',
          detail: 'Review detected items and export for de-identification or compliance'
        }
      ],
      exampleData: 'Patient: John Smith, DOB: 01/15/1980, MRN: 12345, Phone: 555-1234',
      exampleOutput: 'PII Found: Name:John Smith, Date:01/15/1980, MRN:12345, Phone:555-1234',
      labelingMode: 'pii',
      customInstructions: ''
    },
    patientFeedback: {
      title: 'Patient Feedback Classification',
      icon: 'clipboard',
      color: 'green',
      description: 'Analyze patient reviews, feedback, and survey responses',
      steps: [
        {
          step: 1,
          title: 'Create a Project',
          detail: 'Click "New Project" and name it (e.g., "Patient Satisfaction Analysis")'
        },
        {
          step: 2,
          title: 'Upload Feedback Data',
          detail: 'Upload patient reviews, comments, or survey responses'
        },
        {
          step: 3,
          title: 'Choose Analysis Type',
          detail: 'Select "Sentiment Analysis" for positive/negative detection OR "Classification" for categorization'
        },
        {
          step: 4,
          title: 'Add Categories (Optional)',
          detail: 'For classification, specify: "Categorize as: Service Quality, Staff Behavior, Facility, Wait Time, Treatment Outcome"'
        },
        {
          step: 5,
          title: 'Run Analysis',
          detail: 'Click "Label All" to analyze all feedback instantly'
        }
      ],
      exampleData: 'The nurses were incredibly kind and attentive. However, the wait time was too long.',
      exampleOutput: 'Sentiment: Mixed (Positive about staff, Negative about wait time)',
      labelingMode: 'sentiment',
      customInstructions: 'Analyze sentiment and identify specific aspects mentioned (staff, facility, wait time, treatment)'
    },
    medicalResearch: {
      title: 'Medical Research Data',
      icon: 'beaker',
      color: 'purple',
      description: 'Label research data, clinical trial notes, and medical literature',
      steps: [
        {
          step: 1,
          title: 'Create a Project',
          detail: 'Click "New Project" and name it (e.g., "Clinical Trial Data Labeling")'
        },
        {
          step: 2,
          title: 'Upload Research Data',
          detail: 'Upload clinical trial notes, research findings, or literature excerpts'
        },
        {
          step: 3,
          title: 'Select Labeling Type',
          detail: 'Choose "Classification" for outcomes, "NER" for findings, or "Keywords" for topics'
        },
        {
          step: 4,
          title: 'Define Categories',
          detail: 'Specify categories like: "Outcome: Positive, Negative, Neutral, Inconclusive" or extract "adverse events"'
        },
        {
          step: 5,
          title: 'Label Research Data',
          detail: 'Click "Label All" to systematically categorize all research data'
        }
      ],
      exampleData: 'Trial showed 75% efficacy in reducing symptoms. Minor side effects: headache (10%), nausea (5%).',
      exampleOutput: 'Outcome: Positive, Efficacy: 75%, Adverse Events: Headache(10%), Nausea(5%)',
      labelingMode: 'classification',
      customInstructions: 'Classify trial outcomes as Positive, Negative, Neutral, or Inconclusive. Extract efficacy rates and adverse events with percentages.'
    }
  };

  const openHealthcareGuide = (useCaseKey) => {
    setSelectedUseCase(healthcareUseCases[useCaseKey]);
    setHealthcareGuideOpen(true);
  };

  const createProjectFromUseCase = () => {
    setHealthcareGuideOpen(false);
    setNewProject({ 
      name: selectedUseCase.title, 
      description: selectedUseCase.description 
    });
    setIsCreateOpen(true);
  };

  const features = {
    batchUpload: {
      title: 'Batch Upload',
      icon: 'upload',
      color: 'purple',
      description: 'Upload text files or CSV data for instant batch processing',
      steps: [
        {
          step: 1,
          title: 'Create a Project First',
          detail: 'Click "New Project" button and give your project a name (e.g., "Customer Reviews")'
        },
        {
          step: 2,
          title: 'Navigate to Workspace',
          detail: 'Click on your newly created project card to enter the workspace'
        },
        {
          step: 3,
          title: 'Find the Upload Zone',
          detail: 'Look for the "Upload Data" card in the left sidebar with the upload icon'
        },
        {
          step: 4,
          title: 'Prepare Your Data File',
          detail: 'Format as TXT (one item per line) or CSV (each row is an item). Save the file.'
        },
        {
          step: 5,
          title: 'Click or Drag to Upload',
          detail: 'Click the upload zone or drag your file. Supports thousands of items instantly.'
        },
        {
          step: 6,
          title: 'Verify Upload',
          detail: 'Check the statistics card to see total items uploaded successfully'
        }
      ],
      exampleData: 'TXT Format:\nGreat product, highly recommend!\nTerrible experience, never again\nOkay, nothing special\n\nCSV Format:\nid,text\n1,Great product\n2,Terrible experience',
      exampleOutput: 'Success! Uploaded 3 items to your project',
      tips: [
        'TXT files: One text item per line',
        'CSV files: System reads all columns as one text item',
        'Supports files with thousands of rows',
        'Upload progress shows in real-time'
      ]
    },
    aiModes: {
      title: '15+ AI Labeling Modes',
      icon: 'zap',
      color: 'violet',
      description: 'Choose from 15+ specialized AI modes powered by GPT-5.1',
      steps: [
        {
          step: 1,
          title: 'Go to Your Workspace',
          detail: 'Click on any project to open the workspace'
        },
        {
          step: 2,
          title: 'Find the AI Auto-Label Section',
          detail: 'Located in the left sidebar with purple gradient background'
        },
        {
          step: 3,
          title: 'Open Labeling Mode Dropdown',
          detail: 'Click the dropdown to see all 15+ modes organized by category'
        },
        {
          step: 4,
          title: 'Choose Your Mode',
          detail: 'Select based on your goal:\n• Classification - Categorize text\n• NER - Extract entities\n• Sentiment - Positive/Negative\n• Emotion - Joy, Anger, Sadness\n• PII - Detect personal info\n• And 10+ more!'
        },
        {
          step: 5,
          title: 'Add Custom Instructions (Optional)',
          detail: 'Type specific categories or requirements in the text area'
        },
        {
          step: 6,
          title: 'Click "Label All"',
          detail: 'GPT-5.1 processes all unlabeled items instantly'
        }
      ],
      modes: [
        { category: 'Basic', items: ['Classification', 'Topic Modeling', 'Keywords', 'Summarization'] },
        { category: 'Sentiment & Emotion', items: ['Sentiment Analysis', 'Emotion Detection'] },
        { category: 'Extraction', items: ['Named Entities (NER)', 'PII Detection'] },
        { category: 'Intent', items: ['Intent Detection', 'Q&A Generation'] },
        { category: 'Moderation', items: ['Toxicity', 'Spam Detection'] },
        { category: 'Properties', items: ['Language', 'Formality', 'Urgency'] }
      ],
      exampleData: 'Text: "I love this product, it\'s amazing!"\n\nSentiment Mode: Positive\nEmotion Mode: Joy\nClassification Mode: Product Review',
      tips: [
        'Each mode has a description below the dropdown',
        'Try different modes on same data for different insights',
        'Use custom instructions for specific categories',
        'All modes powered by GPT-5.1 for accuracy'
      ]
    },
    reviewExport: {
      title: 'Review & Export',
      icon: 'target',
      color: 'emerald',
      description: 'Review AI-generated labels and export in JSON or CSV format',
      steps: [
        {
          step: 1,
          title: 'View Labeled Items',
          detail: 'After AI labeling, all items appear in the right panel with their labels'
        },
        {
          step: 2,
          title: 'Review Each Label',
          detail: 'Check AI-generated labels (marked with purple "AI" badge)'
        },
        {
          step: 3,
          title: 'Edit if Needed',
          detail: 'Click "Edit" button next to any label, type new label, press Enter'
        },
        {
          step: 4,
          title: 'Mark as Reviewed',
          detail: 'Edited labels automatically marked as reviewed (green checkmark)'
        },
        {
          step: 5,
          title: 'Export Your Data',
          detail: 'Click "JSON" or "CSV" button in the header to download'
        },
        {
          step: 6,
          title: 'Use Exported Data',
          detail: 'JSON: Machine learning training | CSV: Excel analysis'
        }
      ],
      exampleData: 'Before:\nText: "Great service!"\nLabel: [Unlabeled]\n\nAfter Review:\nText: "Great service!"\nLabel: Positive ✓\nReviewed: Yes',
      exportFormats: [
        { format: 'JSON', use: 'Machine learning model training', structure: '[{"id": "1", "text": "...", "label": "..."}]' },
        { format: 'CSV', use: 'Excel, Google Sheets analysis', structure: 'id,text,label,confidence,reviewed\n1,"Great!","Positive",0.95,true' }
      ],
      tips: [
        'Review labels before exporting for best quality',
        'Green checkmark = manually reviewed',
        'Purple "AI" badge = AI-generated',
        'Export includes confidence scores'
      ]
    }
  };

  const openFeatureGuide = (featureKey) => {
    setSelectedFeature(features[featureKey]);
    setFeatureGuideOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Top Navigation Bar */}
      <div className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="text-xl font-bold">
                <span className="gradient-text">JTA</span>
                <span className="text-slate-800 ml-2">Data Labeling</span>
              </div>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">Admin</span>
            </div>
            <div className="flex items-center gap-3">
              <HelpCenter />
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <Home className="w-4 h-4 mr-2" />
                Main Site
              </Button>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Hero Section */}
      <div className="relative overflow-hidden">
        <div className="hero-gradient absolute inset-0" />
        <div className="grid-pattern absolute inset-0 opacity-30" />
        <div className="relative px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white mb-6">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-semibold">AI-Powered Data Labeling</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              JTA AI Data Labeling Tool
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
              Label your data 10x faster with GPT-5.1 AI. Upload, label, and export in minutes.
            </p>
            <Button 
              size="lg" 
              className="btn-modern text-white h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg rounded-full shadow-2xl"
              onClick={() => setIsCreateOpen(true)}
              data-testid="hero-create-project-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New Project
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 sm:-mt-12 mb-8 sm:mb-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <Card className="stat-card border-0" data-testid="total-projects-stat">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 truncate">Projects</p>
                  <p className="text-2xl sm:text-3xl font-bold gradient-text">{projects.length}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0">
                  <Database className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card border-0" data-testid="total-items-stat">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 truncate">Total Items</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-800">
                    {projects.reduce((sum, p) => sum + p.total_items, 0)}
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card border-0" data-testid="labeled-items-stat">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 truncate">Labeled</p>
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600">
                    {projects.reduce((sum, p) => sum + p.labeled_items, 0)}
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card border-0" data-testid="ai-powered-stat">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 truncate">Powered By</p>
                  <p className="text-xl sm:text-2xl font-bold gradient-text">GPT-5.1</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">Your Projects</h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">Manage and organize your labeling projects</p>
          </div>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-modern rounded-full h-11 sm:h-12 px-5 sm:px-6 whitespace-nowrap" data-testid="create-project-btn">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-0 shadow-2xl sm:rounded-2xl max-w-md mx-4">
              <DialogHeader>
                <DialogTitle className="text-xl sm:text-2xl">Create New Project</DialogTitle>
                <DialogDescription className="text-sm sm:text-base">
                  Start a new data labeling project with AI assistance
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="project-name" className="text-sm font-medium">Project Name</Label>
                  <Input
                    id="project-name"
                    data-testid="project-name-input"
                    className="mt-1.5 h-11"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    placeholder="E.g., Customer Feedback Analysis"
                  />
                </div>
                <div>
                  <Label htmlFor="project-desc" className="text-sm font-medium">Description (Optional)</Label>
                  <Textarea
                    id="project-desc"
                    data-testid="project-description-input"
                    className="mt-1.5"
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    placeholder="Describe your project..."
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={createProject} 
                  disabled={loading}
                  className="w-full btn-modern h-11 rounded-full"
                  data-testid="create-project-submit-btn"
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-300 bg-white" data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-12 sm:py-20 px-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-100 to-slate-100 flex items-center justify-center mb-4 sm:mb-6">
                <FolderOpen className="w-8 h-8 sm:w-10 sm:h-10 text-blue-700" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 text-center">No Projects Yet</h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8 text-center max-w-md px-4">Create your first project to start labeling data with AI assistance</p>
              <Button onClick={() => setIsCreateOpen(true)} className="btn-modern rounded-full h-11 sm:h-12 px-6 sm:px-8" data-testid="empty-create-project-btn">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Create Your First Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {projects.map((project) => (
              <Card 
                key={project.id} 
                className="stat-card border-0 cursor-pointer group"
                onClick={() => navigate(`/workspace/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                      <Database className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <span className="px-2 sm:px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                      {project.status}
                    </span>
                  </div>
                  <CardTitle className="text-lg sm:text-xl line-clamp-2 mb-2">{project.name}</CardTitle>
                  <CardDescription className="line-clamp-2 text-sm">
                    {project.description || 'No description'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Total Items</span>
                      <span className="font-semibold text-slate-800">{project.total_items}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Labeled</span>
                      <span className="font-semibold text-emerald-600">{project.labeled_items}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-blue-600 to-blue-800 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${project.total_items > 0 ? (project.labeled_items / project.total_items * 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground pt-2 sm:pt-3 border-t">
                      Created {formatDate(project.created_at)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Features Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-12 sm:mt-20">
          <Card 
            className="border-0 bg-gradient-to-br from-blue-50 to-slate-50 cursor-pointer hover:shadow-2xl transition-all"
            onClick={() => openFeatureGuide('batchUpload')}
            data-testid="batch-upload-card"
          >
            <CardHeader className="p-4 sm:p-6">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center mb-3 sm:mb-4">
                <Upload className="w-6 h-6 sm:w-7 sm:h-7 text-blue-700" />
              </div>
              <CardTitle className="text-lg sm:text-xl">Batch Upload</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <p className="text-sm sm:text-base text-muted-foreground">
                Upload text files or CSV data for instant batch processing. Supports thousands of items.
              </p>
              <p className="text-xs text-blue-700 font-semibold mt-3">Click for step-by-step guide →</p>
            </CardContent>
          </Card>
          
          <Card 
            className="border-0 bg-gradient-to-br from-slate-50 to-blue-50 cursor-pointer hover:shadow-2xl transition-all"
            onClick={() => openFeatureGuide('aiModes')}
            data-testid="ai-modes-card"
          >
            <CardHeader className="p-4 sm:p-6">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center mb-3 sm:mb-4">
                <Zap className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
              </div>
              <CardTitle className="text-lg sm:text-xl">15+ AI Modes</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <p className="text-sm sm:text-base text-muted-foreground">
                Sentiment, NER, emotions, toxicity, language detection, and more - all powered by GPT-5.1.
              </p>
              <p className="text-xs text-blue-600 font-semibold mt-3">Click for mode selection guide →</p>
            </CardContent>
          </Card>
          
          <Card 
            className="border-0 bg-gradient-to-br from-emerald-50 to-slate-50 cursor-pointer hover:shadow-2xl transition-all"
            onClick={() => openFeatureGuide('reviewExport')}
            data-testid="review-export-card"
          >
            <CardHeader className="p-4 sm:p-6">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center mb-3 sm:mb-4">
                <Target className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-600" />
              </div>
              <CardTitle className="text-lg sm:text-xl">Review & Export</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <p className="text-sm sm:text-base text-muted-foreground">
                Review AI-generated labels, make edits, and export in JSON or CSV format instantly.
              </p>
              <p className="text-xs text-emerald-600 font-semibold mt-3">Click for review workflow →</p>
            </CardContent>
          </Card>
        </div>

        {/* Healthcare Use Cases Section */}
        <div className="mt-20 sm:mt-24">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full text-blue-800 mb-4">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-semibold">Healthcare Applications</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Specialized for Healthcare Data</h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
              JTA AI Data Labeling is perfect for healthcare organizations working with medical records, clinical notes, and patient data
            </p>
            
            {/* HIPAA Compliance Badge */}
            <div className="mt-6 inline-flex items-center gap-3 bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-3 rounded-full border-2 border-green-200">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div className="text-left">
                <p className="text-sm font-bold text-green-800">HIPAA Compliance Ready</p>
                <p className="text-xs text-green-700">Built-in PII/PHI detection for secure data handling</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <Card 
              className="border-0 bg-white shadow-lg cursor-pointer hover:shadow-2xl transition-all"
              onClick={() => openHealthcareGuide('clinicalNotes')}
              data-testid="clinical-notes-card"
            >
              <CardHeader className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="text-xl mb-2">Clinical Notes Analysis</CardTitle>
                    <CardDescription>
                      Extract medical entities (diagnoses, medications, procedures) from unstructured clinical notes using Named Entity Recognition
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    <span>Identify symptoms, diagnoses, and treatments</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    <span>Extract medication names and dosages</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    <span>Detect medical procedures and test results</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="border-0 bg-white shadow-lg cursor-pointer hover:shadow-2xl transition-all"
              onClick={() => openHealthcareGuide('piiDetection')}
              data-testid="pii-detection-card"
            >
              <CardHeader className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="text-xl mb-2">PII & PHI Detection</CardTitle>
                    <CardDescription>
                      Automatically detect and classify Protected Health Information (PHI) and Personally Identifiable Information (PII) in medical records
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                    <span>Detect patient names, dates, and addresses</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                    <span>Identify medical record numbers (MRN)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                    <span>HIPAA compliance data preparation</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="border-0 bg-white shadow-lg cursor-pointer hover:shadow-2xl transition-all"
              onClick={() => openHealthcareGuide('patientFeedback')}
              data-testid="patient-feedback-card"
            >
              <CardHeader className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="text-xl mb-2">Patient Feedback Classification</CardTitle>
                    <CardDescription>
                      Analyze patient reviews, feedback, and survey responses to improve healthcare quality and patient satisfaction
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span>Sentiment analysis of patient reviews</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span>Categorize feedback by department/service</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span>Detect urgent issues and complaints</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="border-0 bg-white shadow-lg cursor-pointer hover:shadow-2xl transition-all"
              onClick={() => openHealthcareGuide('medicalResearch')}
              data-testid="medical-research-card"
            >
              <CardHeader className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="text-xl mb-2">Medical Research Data</CardTitle>
                    <CardDescription>
                      Label research data, clinical trial notes, and medical literature for machine learning model training and research analysis
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                    <span>Categorize clinical trial outcomes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                    <span>Extract key findings from literature</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                    <span>Label adverse events and side effects</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Feature Guide Modal */}
      <Dialog open={featureGuideOpen} onOpenChange={setFeatureGuideOpen}>
        <DialogContent className="bg-white border-0 shadow-2xl sm:rounded-2xl max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
          {selectedFeature && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl sm:text-3xl">
                  {selectedFeature.title}
                </DialogTitle>
                <DialogDescription className="text-base">
                  {selectedFeature.description}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 mt-6">
                {/* Step-by-Step Guide */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">How to Use</h3>
                  <div className="space-y-4">
                    {selectedFeature.steps.map((step) => (
                      <div key={step.step} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-bold text-sm">
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
                          <p className="text-sm text-gray-600 whitespace-pre-line">{step.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Modes List (only for aiModes feature) */}
                {selectedFeature.modes && (
                  <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">All Available Modes</h3>
                    <div className="space-y-4">
                      {selectedFeature.modes.map((category, idx) => (
                        <div key={idx}>
                          <p className="text-xs font-semibold text-blue-700 uppercase mb-2">{category.category}</p>
                          <div className="flex flex-wrap gap-2">
                            {category.items.map((mode, mIdx) => (
                              <span key={mIdx} className="px-3 py-1 bg-white rounded-full text-sm border border-blue-200 text-gray-700">
                                {mode}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Export Formats (only for reviewExport feature) */}
                {selectedFeature.exportFormats && (
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Export Formats</h3>
                    <div className="space-y-4">
                      {selectedFeature.exportFormats.map((format, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-4 border border-emerald-200">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-3 py-1 bg-emerald-600 text-white text-xs font-bold rounded-full">
                              {format.format}
                            </span>
                            <span className="text-sm text-gray-600">Best for: {format.use}</span>
                          </div>
                          <p className="text-xs font-mono bg-gray-50 p-3 rounded-lg border border-gray-200 overflow-x-auto">
                            {format.structure}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Example */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Example</h3>
                  <p className="text-sm bg-white rounded-lg p-4 border border-gray-200 font-mono whitespace-pre-line">
                    {selectedFeature.exampleData}
                  </p>
                  {selectedFeature.exampleOutput && (
                    <>
                      <p className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Result:</p>
                      <p className="text-sm bg-white rounded-lg p-3 border border-gray-200 font-mono text-green-700">
                        {selectedFeature.exampleOutput}
                      </p>
                    </>
                  )}
                </div>

                {/* Tips */}
                <div className="bg-blue-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">💡 Pro Tips</h3>
                  <ul className="space-y-2">
                    {selectedFeature.tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></div>
                        <span className="text-sm text-gray-700">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Button */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => setFeatureGuideOpen(false)}
                    className="flex-1 btn-modern h-12 rounded-full"
                  >
                    Got It!
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Healthcare Use Case Guide Modal */}
      <Dialog open={healthcareGuideOpen} onOpenChange={setHealthcareGuideOpen}>
        <DialogContent className="bg-white border-0 shadow-2xl sm:rounded-2xl max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
          {selectedUseCase && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl sm:text-3xl">
                  {selectedUseCase.title}
                </DialogTitle>
                <DialogDescription className="text-base">
                  {selectedUseCase.description}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 mt-6">
                {/* Step-by-Step Guide */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Step-by-Step Guide</h3>
                  <div className="space-y-4">
                    {selectedUseCase.steps.map((step) => (
                      <div key={step.step} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-bold text-sm">
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
                          <p className="text-sm text-gray-600">{step.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Example */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Example</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Input Data:</p>
                      <p className="text-sm bg-white rounded-lg p-3 border border-gray-200 font-mono">
                        {selectedUseCase.exampleData}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">AI Output:</p>
                      <p className="text-sm bg-white rounded-lg p-3 border border-gray-200 font-mono text-green-700">
                        {selectedUseCase.exampleOutput}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recommended Settings */}
                <div className="bg-blue-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Recommended Settings</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5"></div>
                      <div>
                        <span className="font-semibold text-sm">Labeling Mode:</span>
                        <span className="text-sm text-gray-700 ml-2">{selectedUseCase.labelingMode === 'ner' ? 'Named Entity Recognition' : selectedUseCase.labelingMode === 'pii' ? 'PII Detection' : selectedUseCase.labelingMode === 'sentiment' ? 'Sentiment Analysis' : 'Classification'}</span>
                      </div>
                    </div>
                    {selectedUseCase.customInstructions && (
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5"></div>
                        <div>
                          <span className="font-semibold text-sm">Custom Instructions:</span>
                          <p className="text-sm text-gray-700 mt-1 bg-white rounded-lg p-3 border border-blue-200">
                            {selectedUseCase.customInstructions}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={createProjectFromUseCase}
                    className="flex-1 btn-modern h-12 rounded-full"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create Project for This Use Case
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setHealthcareGuideOpen(false)}
                    className="rounded-full h-12 px-6"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Onboarding Tour */}
      <OnboardingTour />
      
      {/* Help Center */}
      <HelpCenter />
      
      {/* AI Chat Assistant */}
      <ChatAssistant />
    </div>
  );
};

export default Dashboard;